// format — pure helpers for the Meetups admin pages (Task 8, Groups Phase 3).
// No supabase/React imports, so these are directly unit-testable.
//
// Meetups are stored as a UTC `starts_at`/`ends_at` plus an IANA `tz` — the
// admin, like the mobile app, must render "when" in the MEETUP's own time
// zone (an organizer in Miami scheduling a run for alumni in New York wants
// "6:30 AM" to mean 6:30 AM there, not wherever the admin happens to be
// sitting), never the browser's local zone.

/** Row shape `formatMeetupStart` needs — a subset of `Tables<'group_meetups'>`. */
export interface MeetupTimeFields {
  starts_at: string;
  tz: string;
}

/**
 * "2030-09-21T10:30:00.000Z" + "America/New_York" → "Sep 21, 2030, 6:30 AM EDT".
 * Falls back to the raw ISO string for an unparseable date or unknown IANA
 * zone — a malformed row should degrade the column's text, not crash the
 * table. Normalizes the narrow no-break space (U+202F) some Intl
 * implementations put before AM/PM to a plain space, same reasoning as
 * backend/supabase/functions/_shared/groupEvents.ts's meetupTimeShort.
 */
export function formatMeetupStart(meetup: MeetupTimeFields): string {
  const d = new Date(meetup.starts_at);
  if (Number.isNaN(d.getTime()) || !meetup.tz) return meetup.starts_at;
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: meetup.tz,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    })
      .format(d)
      .replace(/ /g, ' ');
  } catch {
    return meetup.starts_at;
  }
}

/** "2030-09-21T10:30:00.000Z" + "America/New_York" → "2030-09-21" (the calendar date IN that zone, not UTC's). */
function localDate(iso: string, tz: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime()) || !tz) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(d);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    const y = get('year');
    const m = get('month');
    const day = get('day');
    return y && m && day ? `${y}-${m}-${day}` : '';
  } catch {
    return '';
  }
}

/** Row shape `meetupVideoWindowStatus` needs — a subset of `Tables<'group_meetups'>`. */
export interface MeetupVideoWindowFields {
  format: string;
  status: string;
  starts_at: string;
  ends_at: string;
}

export type MeetupVideoWindowStatus = 'not_video' | 'cancelled' | 'upcoming' | 'open' | 'closed';

/**
 * A DISPLAY-ONLY hint for the Meetups list's "Video" column — never the
 * access decision. The authoritative gate is the backend's
 * `video_join_status` RPC (20261007120000_group_calls.sql, Groups Phase 5
 * Task 1), called PER-MEMBER by the `jaas-token` Edge Function; this admin
 * page has no caller to check membership/moderator status for, so it can
 * only ever answer "is the window open right now, for anyone" — the same
 * starts−15m…ends+60m math `video_join_status` uses for a meetup, mirrored
 * here (not re-derived from a shared import — the backend function is SQL,
 * this one is TypeScript) purely so admins reviewing the Meetups list can
 * see at a glance whether a video room would currently accept joiners.
 *
 * - 'not_video' — format is in_person (there is no room at all)
 * - 'cancelled' — the meetup was cancelled
 * - 'upcoming'  — video meetup, window not open yet (more than 15 min out)
 * - 'open'      — video meetup, inside its join window right now
 * - 'closed'    — video meetup, more than 60 minutes past its end
 */
export function meetupVideoWindowStatus(meetup: MeetupVideoWindowFields, now: Date = new Date()): MeetupVideoWindowStatus {
  if (meetup.format === 'in_person') return 'not_video';
  if (meetup.status === 'cancelled') return 'cancelled';
  const starts = new Date(meetup.starts_at);
  const ends = new Date(meetup.ends_at);
  if (Number.isNaN(starts.getTime()) || Number.isNaN(ends.getTime())) return 'closed';
  const opensAt = starts.getTime() - 15 * 60_000;
  const closesAt = ends.getTime() + 60 * 60_000;
  const t = now.getTime();
  if (t < opensAt) return 'upcoming';
  if (t > closesAt) return 'closed';
  return 'open';
}

/** Row shape `meetupToEventDraft` reads from — a subset of `Tables<'group_meetups'>`. */
export interface MeetupForPromote {
  title: string;
  details: string | null;
  starts_at: string;
  ends_at: string;
  tz: string;
  venue_name: string | null;
  address: string | null;
}

/** The subset of `events` columns this draft fills — every other events column is left for the admin to fill in on the opened Edit page. */
export interface EventDraft {
  title: string;
  description: string | null;
  start_date: string;
  end_date: string;
  location: string | null;
}

/**
 * "Promote to Event" — maps a meetup onto a NEW DRAFT `events` row. `events`
 * (backend/supabase/migrations/20260613240000_events.sql +
 * 20260620000000/20260701210000/20260716120000/20260728140000) has no
 * status/draft column and no time-of-day columns (`start_date`/`end_date`
 * are DATE, not timestamptz) — "draft" here just means minimally filled and
 * opened straight into Edit, the same as any brand-new event created by
 * hand, never a state the schema tracks. Only columns that exist on both
 * sides are mapped: title→title, details→description, the meetup's own-tz
 * calendar dates→start_date/end_date (the time of day has nowhere to go —
 * `events` has no time column), venue_name+address→location. Everything
 * else on `events` (subtitle, cheddarup_url, is_featured, image_url, …) is
 * left null/default for the admin to fill in on the Edit page that opens
 * right after this insert.
 */
export function meetupToEventDraft(meetup: MeetupForPromote): EventDraft {
  const location =
    [meetup.venue_name, meetup.address].filter((v): v is string => Boolean(v && v.length > 0)).join(', ') || null;
  return {
    title: meetup.title,
    description: meetup.details,
    start_date: localDate(meetup.starts_at, meetup.tz),
    end_date: localDate(meetup.ends_at, meetup.tz),
    location,
  };
}
